// Utility helpers for validating date/time digits coming from inputs.

// Check if a value is a finite integer
const isInteger = (n) => Number.isFinite(n) && Number.isInteger(n)

export const isLeapYear = (year) => {
  if (!isInteger(year)) return false
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export const daysInMonth = (year, month) => {
  if (!isInteger(month) || month < 1 || month > 12) return undefined

  const days = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  if (month === 2 && isLeapYear(year)) return 29
  return days[month]
}

export const isValidDate = (year, month, day) => {
  if (!isInteger(year) || !isInteger(month) || !isInteger(day)) return false
  if (month < 1 || month > 12) return false

  const dim = daysInMonth(year, month)
  if (dim === undefined) return false
  return day >= 1 && day <= dim
}

// Parse numeric substrings safely; returns undefined for invalid numbers
const safeParseInt = (str) => {
  if (!str || typeof str !== 'string') return undefined
  if (!/^\d+$/.test(str)) return undefined
  const v = parseInt(str, 10)
  return Number.isFinite(v) ? v : undefined
}

// Validate partial date where `digits` is a contiguous numeric string like 'YYYY', 'YYYYMM', 'YYYYMMDD'
export const validatePartialDate = (digits) => {
  if (typeof digits !== 'string') return false
  // Year
  if (digits.length >= 4) {
    const year = safeParseInt(digits.substring(0, 4))
    if (year === undefined) return false
    if (year < 1000) return false
  }

  // Month
  if (digits.length >= 6) {
    const month = safeParseInt(digits.substring(4, 6))
    if (month === undefined) return false
    if (month < 1 || month > 12) return false
  }

  // Day
  if (digits.length >= 8) {
    const year = safeParseInt(digits.substring(0, 4))
    const month = safeParseInt(digits.substring(4, 6))
    const day = safeParseInt(digits.substring(6, 8))
    if (year === undefined || month === undefined || day === undefined) return false

    if (!isValidDate(year, month, day)) return false
  }

  return true
}

// Validate partial time from a contiguous numeric string that contains time parts at offsets (e.g. after date)
// This mirrors previous behavior: hours at positions 8-9 (0-based) and minutes at 10-11.
export const validatePartialTime = (digits) => {
  if (typeof digits !== 'string') return false

  // Hours (positions 8..9)
  if (digits.length >= 10) {
    const hours = safeParseInt(digits.substring(8, 10))
    if (hours === undefined) return false
    if (hours < 0 || hours > 23) return false
  }

  // Minutes (positions 10..11)
  if (digits.length >= 12) {
    const minutes = safeParseInt(digits.substring(10, 12))
    if (minutes === undefined) return false
    if (minutes < 0 || minutes > 59) return false
  }

  return true
}

// Backwards-compatible default export object
export const dateTimeValidation = {
  isValidDate,
  validatePartialDate,
  validatePartialTime,
}
